import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Rocket, Target, DollarSign, Users, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, FileText, Play, Pause, X, Calendar, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectCommandDashboard() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, [planId]);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/execution/plans/${planId}/dashboard`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load project dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getMilestoneStatusColor = (status) => {
    const colors = {
      not_started: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      blocked: 'bg-red-500',
      awaiting_approval: 'bg-yellow-500',
      completed: 'bg-green-500',
      skipped: 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getRiskColor = (level) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      critical: 'text-red-600'
    };
    return colors[level] || 'text-gray-600';
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-muted-foreground">Loading project dashboard...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!dashboard) {
    return (
      <AppShell>
        <div className="p-8">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>Project not found</AlertDescription>
          </Alert>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight mb-2">
                {dashboard.project_name}
              </h1>
              <div className="flex items-center gap-3">
                <Badge variant={dashboard.is_primary ? "default" : "outline"}>
                  {dashboard.is_primary ? "PRIMARY PROJECT" : "SECONDARY"}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {dashboard.status}
                </Badge>
                {dashboard.current_stage && (
                  <Badge variant="secondary" className="capitalize">
                    {dashboard.current_stage.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/execution')}>
                All Projects
              </Button>
            </div>
          </div>

          {/* Inactivity Warning */}
          {dashboard.inactivity_warning && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>PIVOT OR KILL DECISION REQUIRED:</strong> No milestone progress for 30+ days
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Budget Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${dashboard.budget.spent.toLocaleString()} / ${dashboard.budget.estimated.toLocaleString()}
              </div>
              <Progress 
                value={(dashboard.budget.spent / dashboard.budget.estimated) * 100} 
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                ${dashboard.budget.remaining.toLocaleString()} remaining
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                Timeline Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboard.timeline.progress_percentage}%
              </div>
              <Progress value={dashboard.timeline.progress_percentage} className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {dashboard.timeline.milestones_completed} of {dashboard.timeline.milestones_total} milestones
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Vendors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboard.vendor_count}</div>
              <p className="text-xs text-muted-foreground mt-1">Active vendors</p>
            </CardContent>
          </Card>

          <Card className="border-primary/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Rocket className="h-4 w-4" />
                Next Action
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold text-primary">
                {dashboard.next_action || 'No action required'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Heatmap */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Risk Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Budget Risk</div>
                <div className={`text-2xl font-bold capitalize ${getRiskColor(dashboard.risk_heatmap.budget_risk)}`}>
                  {dashboard.risk_heatmap.budget_risk}
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Timeline Risk</div>
                <div className={`text-2xl font-bold capitalize ${getRiskColor(dashboard.risk_heatmap.timeline_risk)}`}>
                  {dashboard.risk_heatmap.timeline_risk}
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Vendor Risk</div>
                <div className={`text-2xl font-bold capitalize ${getRiskColor(dashboard.risk_heatmap.vendor_risk)}`}>
                  {dashboard.risk_heatmap.vendor_risk}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Milestones */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Project Milestones
            </CardTitle>
            <CardDescription>7-stage execution pipeline</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard.milestones.map((milestone, index) => (
                <div 
                  key={milestone.milestone_id}
                  className="border rounded-lg p-4 hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedMilestone(milestone)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${getMilestoneStatusColor(milestone.status)}`} />
                      <div>
                        <h3 className="font-semibold capitalize">
                          Stage {milestone.stage_order}: {milestone.stage.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {milestone.timeline_days} days • ${milestone.budget_min.toLocaleString()} - ${milestone.budget_max.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge className="capitalize">{milestone.status.replace(/_/g, ' ')}</Badge>
                  </div>

                  <div className="pl-5">
                    <div className="text-sm mb-2">
                      <span className="text-muted-foreground">Deliverables:</span>
                      <ul className="mt-1 space-y-1">
                        {milestone.deliverables.slice(0, 3).map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            {milestone.completed_deliverables?.includes(item) ? (
                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                            ) : (
                              <div className="h-3 w-3 rounded-full border border-gray-300" />
                            )}
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {milestone.status === 'completed' && milestone.actual_cost > 0 && (
                      <div className="text-sm text-muted-foreground mt-2">
                        Actual cost: ${milestone.actual_cost.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
